import { Fragment, type ReactNode } from 'react';
import type { PreviewMessage, WidgetPreviewConfig } from './types';
import { WidgetAvatar } from './widget-avatar';
import { MessageTailIcon } from './widget-icons';
import styles from './widget-preview.module.scss';

export type MessageGroupPosition = 'single' | 'first' | 'middle' | 'last';

interface MessageBubbleProps {
  message: PreviewMessage;
  groupPosition: MessageGroupPosition;
  config: WidgetPreviewConfig;
}

const TOKEN_PATTERN = /(\[[^\]]+\]\(https?:\/\/[^\s)]+\)|https?:\/\/[^\s]+|\*\*[^*]+\*\*|_[^_]+_)/g;

const renderInlineMarkdown = (line: string): ReactNode[] =>
  line.split(TOKEN_PATTERN).map((token, index) => {
    const markdownLink = token.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
    if (markdownLink) {
      return (
        <a
          key={`${token}-${index}`}
          href={markdownLink[2]}
          target="_blank"
          rel="noreferrer noopener"
        >
          {markdownLink[1]}
        </a>
      );
    }
    if (/^https?:\/\//.test(token)) {
      return (
        <a key={`${token}-${index}`} href={token} target="_blank" rel="noreferrer noopener">
          {token}
        </a>
      );
    }
    if (token.startsWith('**') && token.endsWith('**')) {
      return <strong key={`${token}-${index}`}>{token.slice(2, -2)}</strong>;
    }
    if (token.startsWith('_') && token.endsWith('_')) {
      return <em key={`${token}-${index}`}>{token.slice(1, -1)}</em>;
    }
    return token;
  });

const MessageText = ({ text }: { text: string }) => (
  <>
    {text.split('\n').map((line, index, lines) => (
      <Fragment key={`${line}-${index}`}>
        {renderInlineMarkdown(line)}
        {index < lines.length - 1 && <br />}
      </Fragment>
    ))}
  </>
);

export const MessageBubble = ({ message, groupPosition, config }: MessageBubbleProps) => {
  const isUser = message.sender === 'user';
  const isLastInGroup = groupPosition === 'single' || groupPosition === 'last';
  const showAvatar = !isUser && isLastInGroup;
  const showTail = config.messages.tailEnabled && isLastInGroup;

  return (
    <div
      className={`${styles.messageRow} ${isUser ? styles.userMessageRow : ''} ${styles[groupPosition]}`}
    >
      {!isUser && (
        <span className={styles.avatarSlot}>
          {showAvatar && (
            <WidgetAvatar appearance={config.appearance} size={32} className={styles.avatar} />
          )}
        </span>
      )}
      <div
        className={`${styles.messageBubble} ${isUser ? styles.userBubble : styles.assistantBubble}`}
        style={{ borderRadius: config.messages.borderRadius }}
      >
        {showTail && (
          <span
            className={`${styles.messageTail} ${isUser ? styles.userMessageTail : styles.assistantMessageTail}`}
          >
            <MessageTailIcon />
          </span>
        )}
        <MessageText text={message.text} />
      </div>
    </div>
  );
};
